// Admin-only component: configure the WhatsApp bridge payment settings.
// Only shown to super_admin / Direction users in the Settings page.

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { Button, Label } from "@cieda/djayma-ui";
import { adminFetch } from "@/lib/admin-api/base";
import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WhatsappConfig {
  enabled: boolean;
  whatsappNumber: string;
  waveNumber: string;
  orangeNumber: string;
}

// ---------------------------------------------------------------------------
// Sub-component: phone input with +221 prefix badge
// ---------------------------------------------------------------------------

function PhoneInput({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  // Strip the +221 prefix for display; keep raw digits in state
  const displayValue = value.replace(/^\+221/, "").replace(/\s/g, "");

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
    onChange(digits ? `+221${digits}` : "");
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex">
        <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground select-none">
          +221
        </span>
        <input
          id={id}
          type="tel"
          disabled={disabled}
          className="flex h-10 flex-1 rounded-l-none rounded-r-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
          value={displayValue}
          onChange={handleChange}
          placeholder="77 XXX XX XX"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle switch
// ---------------------------------------------------------------------------

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="font-medium text-sm text-foreground">{label}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          checked ? "bg-green-600" : "bg-input"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Message preview
// ---------------------------------------------------------------------------

function MessagePreview({ config }: { config: WhatsappConfig }) {
  const waveNum = config.waveNumber || "+221 77 XXX XX XX";
  const orangeNum = config.orangeNumber || "+221 77 XXX XX XX";

  const preview = [
    "🛒 *Nouvelle commande Djayma*",
    "",
    "📋 Réf : CMD-20260404-0001",
    "💰 Montant : *75 000 FCFA*",
    `💳 Wave → ${waveNum}`,
    `   Orange Money → ${orangeNum}`,
    "",
    "👤 Fatou Diallo",
    "📍 Hann Mariste, Dakar",
    "",
    "Envoyez le montant exact au numéro ci-dessus puis répondez à ce message.",
    "",
    "Merci ! 🙏",
  ].join("\n");

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">
        Aperçu du message WhatsApp
      </p>
      <div className="rounded-xl bg-green-50 border border-green-200 p-4">
        <pre className="text-xs font-mono text-green-900 whitespace-pre-wrap leading-relaxed">
          {preview}
        </pre>
      </div>
      <p className="text-xs text-muted-foreground">
        Ce message est envoyé automatiquement au client après validation de sa commande.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function WhatsappSettings() {
  const [config, setConfig] = useState<WhatsappConfig>({
    enabled: false,
    whatsappNumber: "",
    waveNumber: "",
    orangeNumber: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load current settings from the public checkout-settings endpoint
  useEffect(() => {
    api.settings
      .checkout()
      .then((data) => {
        setConfig({
          enabled: data.whatsapp_enabled ?? false,
          whatsappNumber: data.whatsapp_number ?? "",
          waveNumber: data.wave_number ?? "",
          orangeNumber: data.orange_number ?? "",
        });
      })
      .catch(() => {
        // Fail open — keep defaults
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await adminFetch("/api/admin/config", {
        method: "PATCH",
        body: JSON.stringify({
          "whatsapp.enabled": String(config.enabled),
          "whatsapp.number": config.whatsappNumber,
          "payment.wave_number": config.waveNumber,
          "payment.orange_number": config.orangeNumber,
        }),
      });
      toast.success("Paramètres WhatsApp sauvegardés");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Échec de la sauvegarde";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 w-48 rounded bg-muted" />
        <div className="h-10 w-full rounded bg-muted" />
        <div className="h-10 w-full rounded bg-muted" />
        <div className="h-10 w-full rounded bg-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable toggle */}
      <Toggle
        checked={config.enabled}
        onChange={(v) => setConfig((c) => ({ ...c, enabled: v }))}
        label="Bridge WhatsApp"
        description="Activer le paiement manuel via WhatsApp (Wave / Orange Money)"
      />

      {/* Phone number inputs */}
      <div className="space-y-4">
        <PhoneInput
          id="wa-whatsapp-number"
          label="Numéro WhatsApp CIEDA"
          value={config.whatsappNumber}
          onChange={(v) => setConfig((c) => ({ ...c, whatsappNumber: v }))}
          disabled={!config.enabled}
        />
        <PhoneInput
          id="wa-wave-number"
          label="Numéro Wave CIEDA"
          value={config.waveNumber}
          onChange={(v) => setConfig((c) => ({ ...c, waveNumber: v }))}
          disabled={!config.enabled}
        />
        <PhoneInput
          id="wa-orange-number"
          label="Numéro Orange Money CIEDA"
          value={config.orangeNumber}
          onChange={(v) => setConfig((c) => ({ ...c, orangeNumber: v }))}
          disabled={!config.enabled}
        />
      </div>

      {/* Live message preview */}
      {config.enabled && <MessagePreview config={config} />}

      {/* Save */}
      <div className="pt-2">
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
        >
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Sauvegarder
        </Button>
      </div>
    </div>
  );
}
